<template>
  <div class="bb-user-statistics">
    <div class="bb-user-statistics-header">
      <div class="bb-user-statistics-header__title">MY STATISTICS</div>
      <BBSelect :options="years.list"
                :placeholder="years.placeholder"
                :clearable="years.clearable"
                :label="years.labelList"
                :value="year"
                :minWidth="true"
                @change="setYear"
                class="bb-user-statistics-header__select"
      />
    </div>
    <div class="bb-user-statistics-body">
      <line-chart v-if="loaded"
                  :chartdata="chartdata"
                  :options="options"
                  :styles="chartStyles"
      />
    </div>
  </div>
</template>

<script>
import LineChart from '@/components/Main/Chart/Chart'
import BBSelect from '@/components/Main/Inputs/BBSelect'
import LoaderFly from '@/components/Main/Loaders/LoaderFly'
import convertNumber from '@/mixin/convertNumber'
import numeral from 'numeral'
import KpiApi from '@/api/Kpi/api'
import {get, reduce, groupBy} from 'lodash'
import moment from 'moment'

export default {
  name: 'UserStatistics',
  mixins: [convertNumber],
  props: ['userKpi'],
  components: {
    LineChart,
    BBSelect,
    LoaderFly
  },
  computed: {
    chartStyles () {
      return {
        position: 'relative',
        height: '456px'
      }
    }
  },
  data () {
    return {
      loaded: false,
      chartdata: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        tooltips: {
          enabled: false,
          custom: function (tooltipModel) {
            let tooltipEl = document.getElementById('chartjs-tooltip')
            if (!tooltipEl) {
              tooltipEl = document.createElement('div')
              tooltipEl.id = 'chartjs-tooltip'
              tooltipEl.className = 'bb-chart-tooltip'
              tooltipEl.innerHTML = '<span></span>'
              document.body.appendChild(tooltipEl)
            }
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0
              return
            }
            tooltipEl.classList.remove('above', 'below', 'no-transform')
            const tooltipClass = tooltipModel.yAlign || 'no-transform'
            tooltipEl.classList.add(tooltipClass)
            const {body} = tooltipModel
            if (body) {
              const {lines} = body[0]
              const changeBody = lines.map(item => {
                const re = /[^0-9.-]+/g
                const itemStr = numeral(+(item.replace(re, ''))).format('0,0.00')
                return `<span class="bold">${itemStr}</span> AED`
              })

              const innerHtml = changeBody[0]

              const tableRoot = tooltipEl.querySelector('span')
              tableRoot.innerHTML = innerHtml
            }
            const position = this._chart.canvas.getBoundingClientRect()
            const {left, top} = position
            const {caretX, caretY, xPadding, yPadding} = tooltipModel
            tooltipEl.style.opacity = 1
            tooltipEl.style.position = 'absolute'
            tooltipEl.style.zIndex = 1
            tooltipEl.style.left = `${(left + caretX) - 30}px`
            tooltipEl.style.top = `${(top + caretY) - 40}px`
            tooltipEl.style.fontFamily = 'SF UI Display'
            tooltipEl.style.fontSize = '11px'
            tooltipEl.style.transition = 'all .2s ease'
            tooltipEl.style.fontStyle = 'normal'
            tooltipEl.style.padding = `${yPadding + xPadding}px`
            tooltipEl.style.pointerEvents = 'none'
          }
        }
      },
      years: {
        id: 'year',
        model: 'year',
        list: [
          {
            id: 1,
            name: `${new Date().getFullYear()}`
          },
          {
            id: 2,
            name: `${new Date().getFullYear() - 1}`
          },
          {
            id: 3,
            name: `${new Date().getFullYear() - 2}`
          }
        ],
        labelList: 'name',
        clearable: false,
        searchable: false
      },
      year: {
        id: 1,
        name: `${new Date().getFullYear()}`
      }
    }
  },
  methods: {
    getKPIChart (query) {
      KpiApi.getKPIChart(query)
        .then(({data: {
          chartResults,
          targetChanges
        }}) => {
          const defaultDataChart = []
          const defaultDataChartChangedTarget = []
          const defaultTargetChanges = []
          const pastChart = []
          const monthArr = [...Array(12)]
          const targetsValue = []
          const target = targetChanges.find(({endDate}) => endDate)
          const targetMonth = target ? new Date(target.endDate).getMonth() : -1
          monthArr.forEach((_, i) => {
            defaultDataChart.push(i <= targetMonth ? 0 : null)
            defaultDataChartChangedTarget.push(i >= targetMonth ? 0 : null)
            defaultTargetChanges.push(0)
            pastChart.push(0)
          })
          chartResults.forEach(item => {
            const format = moment(item.approvedDate).format('M')
            item.date = format
          })
          const group = groupBy(chartResults, 'date')
          Object.keys(group).forEach(groupName => {
            const total = reduce(group[`${groupName}`], (sum, item) => {
              return sum + item.approvedValue
            }, 0)
            const indexMonth = +groupName - 1
            defaultDataChart[indexMonth] = indexMonth <= targetMonth ? total : null
            defaultDataChartChangedTarget[indexMonth] = indexMonth >= targetMonth ? total : null
          })
          const dataSetsChart = [
            {
              borderColor: '#0D7BC4',
              borderWidth: 2,
              pointBackgroundColor: 'white',
              backgroundColor: 'rgba(231, 231, 231, 0.46)',
              pointBorderWidth: 3,
              pointHoverBorderColor: '#0D7BC4',
              pointHoverBorderWidth: 3,
              pointHoverBackgroundColor: 'white',
              radius: 3.5,
              data: defaultDataChart,
              tension: 0.4,
              spanGaps: true
            },
            {
              borderColor: '#0D7BC4',
              pointBackgroundColor: 'white',
              borderWidth: 2,
              backgroundColor: 'rgba(13, 123, 196, 0.2)',
              pointBorderWidth: 3,
              pointHoverBorderColor: '#0D7BC4',
              pointHoverBorderWidth: 3,
              pointHoverBackgroundColor: 'white',
              radius: 3.5,
              data: defaultDataChartChangedTarget,
              spanGaps: true
            },
            {
              backgroundColor: 'transparent',
              borderColor: '#04AB14',
              pointRadius: 0,
              pointHoverRadius: 0,
              pointHoverBorderWidth: 0,
              data: defaultTargetChanges,
              pointHoverBorderColor: 'transparent',
              steppedLine: true,
              lineTension: 0,
              borderDash: [5, 5],
              borderWidth: 1
            }
          ]
          this.$set(this.chartdata, 'datasets', dataSetsChart)
          this.changeTargets(targetChanges, pastChart, defaultTargetChanges, targetsValue)
          const {newTicks, totalItem} = this.setTicks(targetsValue, defaultDataChartChangedTarget)
          const scales = {
            yAxes: [{
              display: true,
              ticks: {
                fontColor: '#B9B9B9',
                min: 0,
                max: totalItem
              },
              afterBuildTicks: function (scale) {
                scale.ticks = newTicks
              },
              gridLines: {
                display: true,
                color: '#F4F4F4',
                drawBorder: false
              }
            }]
          }
          this.$set(this.options, 'scales', scales)
          this.loaded = true
        })
        .catch((err) => {
          console.error(err)
        })
    },
    setDefaultChartData () {
      this.chartdata.datasets.splice(0, this.chartdata.datasets.length)
    },
    changeTargets (targetChanges, pastChart, defaultTargetChanges, targetsValue) {
      targetChanges.forEach(item => {
        const dataTarget = [...Array(12)]
        const {startDate, endDate} = item
        const startMonth = new Date(startDate).getMonth()
        const endMonth = endDate ? new Date(endDate).getMonth() : -1
        const value = get(item, 'value', 0)
        if (endDate) {
          dataTarget.forEach((item, index) => {
            pastChart[index] = index >= startMonth && index <= endMonth ? value : null
          })
          const resultChart = [...pastChart]
          this.chartdata.datasets.push({
            backgroundColor: 'transparent',
            borderColor: '#B9B9B9',
            pointRadius: 0,
            pointHoverRadius: 0,
            pointHoverBorderWidth: 0,
            pointHoverBorderColor: 'transparent',
            steppedLine: true,
            data: resultChart,
            lineTension: 0,
            borderDash: [5, 5],
            borderWidth: 1
          })
        }
        if (endMonth === -1) {
          dataTarget.forEach((item, index) => {
            defaultTargetChanges[index] = index >= startMonth ? value : null
          })
        }
        targetsValue.push(value)
      })
    },
    setTicks (targetsValue, defaultDataChartChangedTarget) {
      const newTicks = [...Array(5)]
      const maxChartData = Math.max(...defaultDataChartChangedTarget)
      const maxTarget = Math.max(...targetsValue)
      const max = maxTarget > maxChartData ? maxTarget : maxChartData
      const percent = (max / 100) * 20
      const totalItem = max + percent
      const totalItemRound = this.setRoundNumber(totalItem)
      const yAxisStep = totalItemRound / 5
      const yAxisStepPercent = yAxisStep * 0.20
      let sum = 0
      const ticks = newTicks.map(() => {
        sum += yAxisStep
        return Math.round(sum)
      })
      targetsValue.forEach(targetItem => {
        const valueTickIndex = ticks.findIndex(item => {
          return !targetsValue.find((el) => el === item) &&
            (item - yAxisStepPercent) <= targetItem &&
            (item + yAxisStepPercent) >= targetItem
        })

        if (valueTickIndex !== -1) {
          ticks.splice(valueTickIndex, 1, targetItem)
        } else {
          ticks.push(targetItem)
        }
      })
      return {
        newTicks: ticks,
        totalItem: totalItemRound
      }
    },
    setRoundNumber (number) {
      const truncNumber = Math.trunc(number)
      if (truncNumber < 10) return
      const numbersAfterTwo = truncNumber.toString().slice(2).length
      return Math.round(truncNumber / Math.pow(10, numbersAfterTwo)) * Math.pow(10, numbersAfterTwo)
    },
    setYear (item) {
      this.loaded = false
      this.year = item
      const kpiId = get(this.userKpi, '_id', '')
      const changeQuery = {
        id: kpiId,
        startDate: moment(`${item.name}-01-01`).utc().format('YYYY-MM-DD'),
        endDate: moment(`${item.name}-01-01`).endOf('year').utc().format('YYYY-MM-DD')
      }
      this.setDefaultChartData()
      this.getKPIChart(changeQuery)
    }
  },
  watch: {
    userKpi () {
      const kpiId = get(this.userKpi, '_id', '')
      const query = {
        id: kpiId,
        startDate: moment(`${this.year.name}-01-01`).utc().format('YYYY-MM-DD'),
        endDate: moment(`${this.year.name}-01-01`).endOf('year').utc().format('YYYY-MM-DD')
      }
      this.getKPIChart(query)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../sass/variables";
@import "../../../../sass/mixins";

.bb-user-statistics {
  @include global-font;
  position: relative;
  padding: 26px 20px;

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 23px;

    &__title {
      font-style: normal;
      font-weight: 600;
      font-size: 14px;
      line-height: 17px;
      color: $color-denim;
    }

    &__select {
      width: 82px;
      height: 28px;
      font-style: normal;
      font-weight: 300;
      font-size: 12px;
      line-height: 14px;
    }
  }
}
</style>
<style lang="scss">
  .bold {
    font-style: normal;
    font-weight: 600;
    font-size: 11px;
    line-height: 13px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Settings/UserCard/UserStatistics/UserStatistics.vue